import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, RefreshCw, Clock, XCircle, CheckCircle2, PlayCircle, DollarSign } from 'lucide-react';
import { usePaymentTransferQueue, useRetryPaymentTransfer, useReconcilePayments } from '@/hooks/usePaymentRetryAdmin';
import { usePaymentTransactions } from '@/hooks/usePaymentTransactions';
import { AchPaymentFailureAnalysis } from './AchPaymentFailureAnalysis';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

export function PaymentRecoveryPanel() {
  const { data: allQueue, isLoading: isLoadingQueue } = usePaymentTransferQueue();
  const { data: failedTransactions, isLoading: isLoadingTx } = usePaymentTransactions({ status: 'failed' });
  const retryTransfer = useRetryPaymentTransfer();
  const reconcile = useReconcilePayments();

  const isLoading = isLoadingQueue || isLoadingTx;

  const formatAmount = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const getStatusBadge = (status: string) => {
    const configs = {
      pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800', icon: Clock },
      processing: { label: 'Processing', className: 'bg-blue-100 text-blue-800', icon: PlayCircle },
      completed: { label: 'Completed', className: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      failed: { label: 'Failed - Retry Scheduled', className: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
      failed_permanently: { label: 'Failed Permanently', className: 'bg-red-100 text-red-800', icon: XCircle },
    };
    const config = configs[status as keyof typeof configs] || { 
      label: status, 
      className: 'bg-gray-100 text-gray-800',
      icon: Clock 
    };
    const Icon = config.icon;
    return (
      <Badge className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // Filter for items needing attention
  const failedItems = allQueue?.filter(item => 
    item.status === 'failed' || item.status === 'failed_permanently'
  ) || [];

  const pendingItems = allQueue?.filter(item => item.status === 'pending') || [];
  const processingItems = allQueue?.filter(item => item.status === 'processing') || [];
  
  // Filter ACH failures
  const achFailures = failedTransactions?.filter(tx => 
    tx.payment_method_type === 'us_bank_account'
  ) || [];

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Loading payment recovery data...
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="transfers" className="space-y-6">
        <TabsList>
          <TabsTrigger value="transfers">Transfer Queue</TabsTrigger>
          <TabsTrigger value="ach-failures">
            ACH Failures
            {achFailures.length > 0 && (
              <Badge variant="destructive" className="ml-2">
                {achFailures.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="transfers" className="space-y-6">
      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Payment Recovery Tools</h3>
          <p className="text-sm text-muted-foreground">
            Manage stuck and failed payment transfers
          </p>
        </div>
        <Button
          onClick={() => reconcile.mutate()}
          disabled={reconcile.isPending}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${reconcile.isPending ? 'animate-spin' : ''}`} />
          Run Reconciliation
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Transfers</p>
                <p className="text-2xl font-bold">{pendingItems.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Failed (Retrying)</p>
                <p className="text-2xl font-bold">
                  {allQueue?.filter(i => i.status === 'failed').length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Permanently Failed</p>
                <p className="text-2xl font-bold">
                  {allQueue?.filter(i => i.status === 'failed_permanently').length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Failed Transfers Alert */}
      {failedItems.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Action Required</AlertTitle>
          <AlertDescription>
            {failedItems.length} payment transfer(s) need attention. Review the table below and retry failed transfers.
          </AlertDescription>
        </Alert>
      )}

      {/* Failed Transfers Table */}
      {failedItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Failed Transfers</CardTitle>
            <CardDescription>Transfers that need manual intervention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Attempts</TableHead>
                    <TableHead>Last Error</TableHead>
                    <TableHead>Next Retry</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {failedItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <Link 
                            to={`/jobs/${item.jobs?.public_id}`}
                            className="text-primary hover:underline font-medium"
                          >
                            {item.jobs?.title}
                          </Link>
                          <span className="text-xs text-muted-foreground font-mono">
                            {item.jobs?.public_id}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatAmount(item.payment_transactions?.vendor_payout_cents || 0)}
                      </TableCell>
                      <TableCell>{getStatusBadge(item.status)}</TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {item.retry_count} / {item.max_retries}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs truncate text-sm text-muted-foreground">
                          {item.last_error || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.next_retry_at 
                          ? format(new Date(item.next_retry_at), 'MMM dd, HH:mm')
                          : 'N/A'
                        }
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => retryTransfer.mutate({ queueId: item.id })}
                          disabled={retryTransfer.isPending}
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Retry Now
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Transfers Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Transfer Queue Items</CardTitle>
          <CardDescription>Complete history of payment transfers</CardDescription>
        </CardHeader>
        <CardContent>
          {!allQueue || allQueue.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No transfer queue items found
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Job</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Attempts</TableHead>
                    <TableHead>Last Attempt</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allQueue.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {format(new Date(item.created_at), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>
                        <Link 
                          to={`/jobs/${item.jobs?.public_id}`}
                          className="text-primary hover:underline text-sm"
                        >
                          {item.jobs?.public_id}
                        </Link>
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatAmount(item.payment_transactions?.vendor_payout_cents || 0)}
                      </TableCell>
                      <TableCell>{getStatusBadge(item.status)}</TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {item.retry_count} / {item.max_retries}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {item.last_attempt_at 
                          ? format(new Date(item.last_attempt_at), 'MMM dd, HH:mm')
                          : 'N/A'
                        }
                      </TableCell>
                      <TableCell>
                        {(item.status === 'failed' || item.status === 'failed_permanently') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => retryTransfer.mutate({ queueId: item.id })}
                            disabled={retryTransfer.isPending}
                          >
                            <RefreshCw className="h-3 w-3" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="ach-failures" className="space-y-6">
          {/* ACH Failures Summary */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">ACH Payment Failures</h3>
              <p className="text-sm text-muted-foreground">
                Detailed analysis of failed ACH payments with recommended actions
              </p>
            </div>
          </div>

          {achFailures.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto text-green-600 mb-4" />
                <p className="text-lg font-medium">No ACH Failures</p>
                <p className="text-sm text-muted-foreground mt-2">
                  All ACH payments are processing successfully
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Summary Card */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                      <DollarSign className="h-5 w-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Failed ACH Payments</p>
                      <p className="text-2xl font-bold">{achFailures.length}</p>
                      <p className="text-xs text-muted-foreground">
                        Total: {formatAmount(achFailures.reduce((sum, tx) => sum + tx.total_amount_cents, 0))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* ACH Failures Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Failed ACH Payments</CardTitle>
                  <CardDescription>
                    Review each failure and take recommended actions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Job ID</TableHead>
                          <TableHead>Account</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Error</TableHead>
                          <TableHead>Retries</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {achFailures.map((transaction) => (
                          <TableRow key={transaction.id}>
                            <TableCell className="text-sm">
                              {format(new Date(transaction.created_at), 'MMM dd, yyyy')}
                            </TableCell>
                            <TableCell>
                              <Link 
                                to={`/jobs/${transaction.job_id}`}
                                className="text-primary hover:underline font-mono text-sm"
                              >
                                {transaction.job_id.slice(0, 8)}...
                              </Link>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <Badge variant="outline" className="w-fit">ACH</Badge>
                                {transaction.payment_method_last4 && (
                                  <span className="text-xs text-muted-foreground">
                                    ••••{transaction.payment_method_last4}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="font-semibold">
                              {formatAmount(transaction.total_amount_cents)}
                            </TableCell>
                            <TableCell>
                              <div className="max-w-xs truncate text-xs text-destructive">
                                {transaction.error_message || 'Unknown error'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {transaction.retry_count}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <AchPaymentFailureAnalysis transaction={transaction} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
